/**
 * Page class is intended to capture different properties for HTML pages
 * Supply those properties for services to use them (ie. analytics to mParticle).
 *
 * Any specific formatting of properties or types should be done in concrete classes for the third-part provider, not
 * here.
 */
import { AdService } from '../services/ads'
import { AgeGateService } from '../services/age-gate'
import { AgeGateEvents, PageAgeGateEvents } from '../services/age-gate/event'
import { PageAgeGateService } from '../services/age-gate/page'
import { AnalyticsService, IAnalyticsServiceConfiguration } from '../services/analytics'
import { IdentityEvent, IIdentityEvent } from '../services/common/events/identity'
import { PageType } from '../services/common/page-types'
import { IProfile, Profile } from '../services/common/profile'
import { SidebarTrendingWidget } from '../services/common/sidebar-trending-widget'
import { ConsentBannerService } from '../services/consent-banner'
import { IdentityService } from '../services/identity'
import { ISubscription } from '../services/identity/adapters/adapter'
import { LoginService } from '../services/identity/adapters/loginradius/services/login-service'
import { Viafoura } from '../services/identity/adapters/viafoura'
import { IdentityFactory } from '../services/identity/factory'
import { RecommendationsService } from '../services/recommendations'
import { WidgetService } from '../services/widgets'
import { Utils } from '../utils/common'
import { logger, loggerTimeLog, TimerLabels } from '../utils/common/logger'
import { Store } from '../utils/storage/keys'
import { IConfiguration } from './conf'

export interface IWindow extends Window {
    sophi: any,
    tp: any
}

declare let window: IWindow

export class Page {
    public profile: IProfile
    public pageStartTime: number
    protected analyticsConf: IAnalyticsServiceConfiguration
    private widgetService = new WidgetService()
    private hasSubscribersConf = false

    constructor(
        private conf: IConfiguration,
        private adService: AdService,
        private ageGateService: AgeGateService,
        private pageAgeGateService: PageAgeGateService,
        private consentBannerService: ConsentBannerService,
        private recommendationsService: RecommendationsService,
        private analyticsService: AnalyticsService,
        private identityService: IdentityService) {
    }

    /**
     * Initialize the page
     */
    public async init(): Promise<Page> {
        logger('debug', `${TimerLabels.PAGE_LOAD} - Page object constructor`)
        loggerTimeLog(TimerLabels.PAGE_LOAD)

        this.reportWebVitals().catch((err) => console.log('error', err))

        this.profile = this.identityService.getProfile() || new Profile()
        logger('debug', 'previously stored profile: ', this.profile)
        // create analytics config
        if (this.conf.analytics && this.analyticsService) {
            this.analyticsConf = {
                adSite: this.conf.ads.site,
                brand: this.conf.analytics.brand,
                debug: this.conf.analytics.debug,
                division: this.conf.analytics.division,
                domain: this.conf.analytics.domain,
                femVersion: this.conf.analytics.femVersion,
                isDev: this.conf.analytics.isDev,
                page: this.conf.page,
                platform: this.conf.analytics.platform,
                platformVersion: this.conf.analytics.platformVersion,
                profile: this.profile,
                provider: this.conf.analytics.provider,
                tags: this.conf.analytics.tags
            }
        }
        // initialize permutive before loading ads
        if (this.conf.analytics && this.conf.analytics.tags) {
           this.analyticsService.tagManager.initializePermutive(this.conf.analytics.tags, this.analyticsConf )
           this.analyticsService.tagManager.initializeLiveRamp(this.conf.analytics.tags)
        }
        // reset Profile in analyticsConf
        this.analyticsConf = {
            ...this.analyticsConf,
            profile: undefined
        }

        // tslint:disable-next-line:no-unused-expression
        new SidebarTrendingWidget(this.conf)

        // if page is age gated, do not wait for ad service to fully initialize to begin initializing other services
        // pages which are age gated require the age gate service to be initialized prior to completing ad service init
        if (this.conf.page && this.conf.page.ageGate && this.conf.page.ageGate.enabled) {
            this.loadAds()
                .then(() => logger('debug', `Ads loaded`))
                .catch((err) => {
                    logger('error', 'Error loading ads: ', err)
                })
        } else {
            await this.loadAds()
            logger('debug', `Ads loaded`)
        }

        // init identity service first; dependent services init'd in change handler
        /* @TODO why are we binding to this event so many times? is it needed */

        document.addEventListener(IdentityEvent.IDENTITY_INIT, this.onIdentityInit.bind(this))
        document.addEventListener(IdentityEvent.IDENTITY_READY, (e: CustomEvent)=> {
            logger('debug', `Identity ready with event:`, e)
            this.onIdentityChange(e)
        })

        if (sessionStorage) {
            let sophiLoaded = false

            if (sessionStorage.getItem(Store.SOPHI_LOADED) === 'true') {
                sophiLoaded = true
            }

            if (!window.sophi || sophiLoaded) {
                // Start identity service if Sophi is already loaded by the time FEM is running
                if (sophiLoaded) {
                    sessionStorage.removeItem(Store.SOPHI_LOADED)
                }
                await this.loadIdentityService()
            } else {
                // Wait until Sophi is loaded before starting identity service
                document.addEventListener(IdentityEvent.IDENTITY_SERVICE_INIT, async () => {
                    sessionStorage.removeItem(Store.SOPHI_LOADED)
                    await this.loadIdentityService()
                })
            }
        }

        if (this.conf.identity) {

            if (this.conf.identity.subscriber) {
                this.hasSubscribersConf = true
            }

            document.addEventListener(IdentityEvent.EMAIL_VERIFIED, this.onEmailVerified.bind(this))
            document.addEventListener(IdentityEvent.IDENTITY_SESSION_PROFILE_INIT, this.onIdentitySession.bind(this))
        } else {
            this.onIdentitySession(Utils.event(IdentityEvent.IDENTITY_SESSION_PROFILE_INIT, this.profile))
        }

        this.pageAgeGateService
            .addEventListener(PageAgeGateEvents.VERIFIED, this.onAgeVerified.bind(this))

        this.ageGateService
            .addEventListener(AgeGateEvents.VERIFIED, this.onAgeVerified.bind(this))

        this.consentBannerService.initialize()
        const pianoParams = sessionStorage && sessionStorage.getItem(Store.PIANO_PARAMS)
        if (this.conf.page.type === PageType.SUBSCRIBE ||
            this.conf.page.type === PageType.STORY) {
            const redirectUrl = new URL(location.href)
            // Remove lr-token parameter from the redirect URL
            if (redirectUrl.searchParams.get('lr-token')) {
                redirectUrl.searchParams.delete('lr-token')
            }
            LoginService.setRedirectUrl(redirectUrl.toString())
        }
        if (pianoParams && this.completeSignIn(this.profile) &&
            this.conf.page.type !== PageType.REGISTER &&
            this.conf.page.type !== PageType.SIGN_IN &&
            this.conf.page.type !== PageType.VERIFY_PASSWORDLESS &&
            this.conf.page.type !== PageType.VERIFY_PASSWORDLESS_ERROR) {
            // If params object is valid and the user is logged in - start checkout
            window.tp.push(['init', () => {
                window.tp.offer.startCheckout(JSON.parse(pianoParams))
            }])
            // sessionStorage.removeItem(Store.PIANO_PARAMS)
        }

        return this
    }

    /**
     * Checks if sign in is complete
     */
    public completeSignIn(profile: IProfile) {
        return (this.hasSubscribersConf) ? profile.email && profile.subscriptions : profile.email
    }

    /**
     * Starts identity service
     */
    public async loadIdentityService() {
        logger('debug', `Identity service initializing`)
        await this.identityService.initialize(this.conf,
                await IdentityFactory.getIdentityInstance(this.conf),
                await IdentityFactory.getSubscriberInstance(this.conf),
                new Viafoura())
        logger('debug', `Identity service initialized`)
    }

    private onAgeVerified() {
        this.identityService.update({ageOfMajority: true} as IProfile).catch(console.log)
    }

    private onEmailVerified(event: IIdentityEvent) {
        // update subscription-related data
        this.profile = event.detail
        this.updateSubscriberDetails()
        this.identityService.update(this.profile).catch(console.log)
    }

    private onIdentityChange(event: IIdentityEvent) {
        logger('debug', `${TimerLabels.PAGE_LOAD} - onIdentityChange event handler`)
        loggerTimeLog(TimerLabels.PAGE_LOAD)
        this.profile = event.detail

        // update subscription-related data (e.g. 'ad light' flag)
        this.updateSubscriberDetails()
        // update analytics service
        if (this.analyticsConf && this.analyticsConf.profile) {
            this.analyticsService.update(this.profile)
        }

        document.dispatchEvent(new Event(IdentityEvent.IDENTITY_PROFILE_UPDATED))

        // update ad service
        this.adService.update(this.profile.adLight && !this.profile.adLightDisabled).catch(console.log)

        // update widget service
        this.widgetService.update(this.profile.adLight).catch(console.log)
    }

    private async loadAds() {
        // initialize ad service
        if (this.conf.ads && this.adService) {
            try {
                await this.adService.initialize({
                    adLight: this.profile.adLight && !this.profile.adLightDisabled,
                    adLightEnabled: this.conf.ads.adLightEnabled,
                    analyticsConfig: this.conf.analytics,
                    ancestry: this.conf.ads.ancestry,
                    articleId: this.conf.page.articleId,
                    bidding: this.conf.ads.bidding,
                    hub: this.conf.ads.hub,
                    negative: this.conf.page.klangoo ? this.conf.page.klangoo.negativeKeywords : undefined,
                    network: this.conf.ads.network,
                    originId: this.conf.page.originId,
                    page: this.conf.ads.type,
                    pageConfig: this.conf.page,
                    property: this.conf.ads.property,
                    provider: this.conf.ads.provider,
                    refresh: this.conf.ads.refresh,
                    sensitive: this.conf.page.klangoo ? this.conf.page.klangoo.sensitiveContent : undefined,
                    siteId: this.conf.ads.site,
                    spoke: this.conf.ads.spoke,
                    stickyAd: this.conf.ads.stickyAd,
                    topics: this.conf.ads.topics,
                    verifier: this.conf.ads.verifier,
                    wpLegacyId: this.conf.page.wpLegacyId,
                    writers: this.conf.page.writers
                })
            } catch (e) {
                logger('error', `adService.initialize failed`, e)
            }
        }
    }

    /**
     * Actions requiring user profile.
     */
    private onIdentityInit(event: IIdentityEvent) {
        this.profile = event.detail

        // update subscription-related data (e.g. 'ad light' flag)
        this.updateSubscriberDetails()
        this.identityService.update(this.profile).catch(console.log)

        // initialize age gate service profile status
        this.ageGateService.initialize(this.profile.ageOfMajority)

        this.pageAgeGateService.initialize(this.conf.page.ageGate, this.profile.ageOfMajority)

        // completly signed in, safe to init analytics
        if (this.analyticsConf && this.completeSignIn(this.profile)) {
            this.analyticsConf = {
                ...this.analyticsConf,
                profile: this.profile
            }
            this.analyticsService.initialize(this.analyticsConf).catch(console.log)
        } else {
            this.onIdentitySession(Utils.event(IdentityEvent.IDENTITY_SESSION_PROFILE_INIT, this.profile))
        }

        // initialize widget service
        this.widgetService.initialize(this.profile.adLight).catch(console.log)

        // initilize recommendationsService
        this.recommendationsService.initialize(this.conf.page.articleId, this.conf.page.isRecommendationEnabled,
            this.conf.page.isWelcomeToMyTopicsModalEnabled, this.profile.recommendationCategories)
    }

    /**
     * Identity session init handler.
     */
    private onIdentitySession(event: IIdentityEvent) {
        this.profile = event.detail

        // user anon and analytics not yet init'd, safe to init
        if (this.analyticsConf && !this.analyticsConf.profile && !this.profile.email) {
            this.analyticsConf = {
                ...this.analyticsConf,
                profile: this.profile
            }
            this.analyticsService.initialize(this.analyticsConf).catch(console.log)
        }
    }

    private updateSubscriberDetails() {
        this.profile.adLight = (this.profile.subscriptions || []).reduce((pass, sub: ISubscription) => {
            return pass || (this.conf.identity.subscriber.resIds || []).indexOf(sub.id) >= 0
        }, false)
    }

    /* Function trigerred on every page load
     * but loads web-vitals script and logs vitals into console
     * only if core-web-vitals-measuring present
     */
    private async reportWebVitals(): Promise<void> {
        const CORE_WEB_VITALS_MEASURING = 'core-web-vitals-measuring'
        const queryParams = new URLSearchParams(window.location.search)
        if (queryParams.has(CORE_WEB_VITALS_MEASURING)) {
            // https://www.npmjs.com/package/web-vitals?kj#load-web-vitals-from-a-cdn
            const url =
                'https://unpkg.com/web-vitals@3.1.1/dist/web-vitals.attribution.js?module'
            // https://webpack.js.org/api/module-methods/#magic-comments
            await import(/* webpackIgnore: true */ url).then((module) => {
                module.onCLS(console.log, {
                    reportAllChanges: true
                })
                module.onFCP(console.log, {
                    reportAllChanges: true
                })
                module.onLCP(console.log, {
                    reportAllChanges: true
                })
                module.onTTFB(console.log, {
                    reportAllChanges: true
                })
                // Interaction to Next Paint and First Input Delay only trigerred on User Interaction
                module.onFID(console.log, {
                    reportAllChanges: true
                })
                module.onINP(console.log, {
                    reportAllChanges: true
                })
            })
        }
    }
}
